import {
  createContext,
  type ReactNode,
  type Ref,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AppHeader, type AppHeaderConfig } from './AppHeader';

// ============================================================================
// ShellHeaderRegistry (W1) — eine Header-Wahrheit fuer Shell und Split-Panes.
// ----------------------------------------------------------------------------
// Jede meldende Komponenteninstanz besitzt einen eigenen Owner-Key. Dadurch
// kann das Cleanup eines alten Routenbaums nie den bereits gemeldeten Header
// seines Nachfolgers loeschen. Der Scope kommt aus Pane; ausserhalb eines
// Splits gilt der Root-Scope der AppShell.
// ============================================================================

export const ROOT_SHELL_HEADER_SCOPE = 'shell';

export type ShellHeaderPaneRole = 'list' | 'detail' | 'inspector';

export function shellPaneHeaderScope(splitId: string, role: ShellHeaderPaneRole): string {
  return `${splitId}:${role}`;
}

interface HeaderEntry {
  owner: string;
  order: number;
  config: AppHeaderConfig;
}

interface SplitEntry {
  id: string;
  order: number;
  hasDetail: boolean;
  isWide: boolean;
  compactIndexPane: 'list' | 'empty';
}

interface ShellHeaderRegistryValue {
  headers: Record<string, HeaderEntry[]>;
  splits: Record<string, SplitEntry>;
  setHeader: (scope: string, owner: string, config: AppHeaderConfig) => void;
  resetHeader: (scope: string, owner: string) => void;
  setSplit: (entry: Omit<SplitEntry, 'order'>) => void;
  resetSplit: (id: string) => void;
}

const ShellHeaderRegistryContext = createContext<ShellHeaderRegistryValue | null>(null);
const ShellHeaderScopeContext = createContext(ROOT_SHELL_HEADER_SCOPE);

function latestHeader(entries: HeaderEntry[] | undefined): AppHeaderConfig | null {
  if (!entries?.length) return null;
  return entries.reduce((latest, entry) => (entry.order > latest.order ? entry : latest)).config;
}

export function ShellHeaderRegistryProvider({ children }: { children: ReactNode }) {
  const sequenceRef = useRef(0);
  const [headers, setHeaders] = useState<Record<string, HeaderEntry[]>>({});
  const [splits, setSplits] = useState<Record<string, SplitEntry>>({});

  const setHeader = useCallback((scope: string, owner: string, config: AppHeaderConfig) => {
    const order = ++sequenceRef.current;
    setHeaders((current) => ({
      ...current,
      [scope]: [
        ...(current[scope] ?? []).filter((entry) => entry.owner !== owner),
        { owner, order, config },
      ],
    }));
  }, []);

  const resetHeader = useCallback((scope: string, owner: string) => {
    setHeaders((current) => {
      const remaining = (current[scope] ?? []).filter((entry) => entry.owner !== owner);
      if (remaining.length === (current[scope] ?? []).length) return current;
      const next = { ...current };
      if (remaining.length) next[scope] = remaining;
      else delete next[scope];
      return next;
    });
  }, []);

  const setSplit = useCallback((entry: Omit<SplitEntry, 'order'>) => {
    const order = ++sequenceRef.current;
    setSplits((current) => ({ ...current, [entry.id]: { ...entry, order } }));
  }, []);

  const resetSplit = useCallback((id: string) => {
    setSplits((current) => {
      if (!(id in current)) return current;
      const next = { ...current };
      delete next[id];
      return next;
    });
  }, []);

  const value = useMemo<ShellHeaderRegistryValue>(() => ({
    headers,
    splits,
    setHeader,
    resetHeader,
    setSplit,
    resetSplit,
  }), [headers, resetHeader, resetSplit, setHeader, setSplit, splits]);

  return (
    <ShellHeaderRegistryContext.Provider value={value}>
      {children}
    </ShellHeaderRegistryContext.Provider>
  );
}

export function ShellHeaderScopeProvider({ scope, children }: { scope: string; children: ReactNode }) {
  return (
    <ShellHeaderScopeContext.Provider value={scope}>
      {children}
    </ShellHeaderScopeContext.Provider>
  );
}

export function useShellHeaderScope(): string {
  return useContext(ShellHeaderScopeContext);
}

export function useShellHeaderRegistry(): ShellHeaderRegistryValue | null {
  return useContext(ShellHeaderRegistryContext);
}

export function useRegisterShellSplit(entry: Omit<SplitEntry, 'order'>): void {
  const registry = useContext(ShellHeaderRegistryContext);
  const setSplit = registry?.setSplit;
  const resetSplit = registry?.resetSplit;
  useLayoutEffect(() => {
    if (!setSplit || !resetSplit) return undefined;
    setSplit(entry);
    return () => resetSplit(entry.id);
  }, [entry.compactIndexPane, entry.hasDetail, entry.id, entry.isWide, resetSplit, setSplit]);
}

export function useRegisteredShellHeader(scope: string): AppHeaderConfig | null {
  const registry = useContext(ShellHeaderRegistryContext);
  return latestHeader(registry?.headers[scope]);
}

/**
 * L1: das globale Chrome ohne Titel/Zurueck. Im breiten Split traegt die EINE
 * Shell-Kopfzeile ueber der ganzen Buehne genau das; Titel, Zurueck-Pfeil und
 * Seiten-Aktionen leben in den Pane-Titelzeilen darunter.
 *
 * `transparent: false` ist Absicht: die durchsichtige Variante ist
 * `position: fixed` (AppHeader.module.css) — im Split braucht die Shell-Zeile
 * aber eine echte Grid-Hoehe, sonst liegt sie ueber der ersten Pane-Zeile bzw.
 * (auf /world) frei auf dem Hero-Bild.
 */
const SHELL_CHROME_ONLY: AppHeaderConfig = Object.freeze({ transparent: false });

/**
 * Unterhalb der Split-Schwelle gewinnt der Header des sichtbaren Pane. Ab der
 * Schwelle liegen die Pane-Header in deren Grid-Zeilen; darueber steht genau
 * EINE Shell-Kopfzeile mit dem globalen Chrome.
 */
export function useActiveShellHeader(): AppHeaderConfig | null {
  const registry = useContext(ShellHeaderRegistryContext);
  if (!registry) return null;
  const activeWideSplit = Object.values(registry.splits)
    .filter((entry) => entry.isWide)
    .sort((left, right) => right.order - left.order)[0];
  if (activeWideSplit) {
    const root = latestHeader(registry.headers[ROOT_SHELL_HEADER_SCOPE]);
    // Ein explizit gemeldeter Root-Header darf Aktionen beisteuern, aber
    // niemals Titel/Zurueck doppeln — die stehen in der Pane-Zeile.
    return root
      ? { ...root, title: undefined, backTo: undefined, transparent: false }
      : SHELL_CHROME_ONLY;
  }
  const activeCompactSplit = Object.values(registry.splits)
    .filter((entry) => !entry.isWide)
    .sort((left, right) => right.order - left.order)[0];
  if (activeCompactSplit) {
    const role: ShellHeaderPaneRole = activeCompactSplit.hasDetail
      || activeCompactSplit.compactIndexPane === 'empty'
      ? 'detail'
      : 'list';
    const paneHeader = latestHeader(
      registry.headers[shellPaneHeaderScope(activeCompactSplit.id, role)],
    );
    if (paneHeader) return paneHeader;
  }
  return latestHeader(registry.headers[ROOT_SHELL_HEADER_SCOPE]);
}

export function useHasRegisteredShellHeaders(): boolean {
  const registry = useContext(ShellHeaderRegistryContext);
  return Boolean(registry && Object.values(registry.headers).some((entries) => entries.length > 0));
}

interface CoordinatedShellHeaderProps {
  placement: 'shell' | 'pane';
  scope?: string;
  /** L1: Aktionen des Layout-Rahmens (Maximieren-Umschalter). */
  extraActions?: ReactNode;
  /** L1: reicht das <header>-Element an den Rahmen durch (Hoehenmessung). */
  elementRef?: Ref<HTMLElement>;
}

/** Eine Render-Komponente fuer den einzelnen Shell-Header und alle Pane-Zeilen. */
export function CoordinatedShellHeader({
  placement,
  scope,
  extraActions,
  elementRef,
}: CoordinatedShellHeaderProps) {
  const activeHeader = useActiveShellHeader();
  const scopedHeader = useRegisteredShellHeader(scope ?? ROOT_SHELL_HEADER_SCOPE);
  // Ohne gemeldete Konfiguration rendert diese Komponente NICHTS. Den
  // Leerzustand des Detail-Pane traegt bewusst Pane.tsx mit einer eigenen
  // schmalen Werkzeugleiste (kein AppHeader → kein I18nProvider, keine
  // Coin-Abfrage). Ein zweiter Weg dafuer (`allowEmpty` + EMPTY_HEADER) war
  // unerreichbar und ist entfernt.
  const config = scope ? scopedHeader : activeHeader;
  if (!config) return null;
  return <AppHeader {...config} placement={placement} extraActions={extraActions} elementRef={elementRef} />;
}
